import { useState, useEffect, useRef, useCallback } from 'react';
import { useIdleTimer } from 'react-idle-timer';

interface UseIdleTimerOptions {
  onIdle: () => void;
  onPrompt: () => void;
  idleTime?: number; // Time before showing prompt (default: 5 minutes)
  promptTime?: number; // Time before auto logout after prompt (default: 5 minutes)
  enabled?: boolean; // Whether idle timer is enabled
}

export function useIdleTimerHook({
  onIdle,
  onPrompt,
  idleTime = 5 * 60 * 1000, // 5 minutes in milliseconds
  promptTime = 5 * 60 * 1000, // 5 minutes in milliseconds
  enabled = true,
}: UseIdleTimerOptions) {
  const [isPrompted, setIsPrompted] = useState(false);
  const isPromptedRef = useRef(false);
  const promptTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const onIdleRef = useRef(onIdle);
  const onPromptRef = useRef(onPrompt);
  const pauseRef = useRef<(() => void) | null>(null);
  const resumeRef = useRef<(() => void) | null>(null);
  const resetRef = useRef<(() => void) | null>(null);

  // Keep refs updated
  useEffect(() => {
    onIdleRef.current = onIdle;
    onPromptRef.current = onPrompt;
    isPromptedRef.current = isPrompted;
  }, [onIdle, onPrompt, isPrompted]);

  const handleOnIdle = useCallback(() => {
    console.log('[IdleTimer] onIdle called, isPrompted:', isPromptedRef.current);
    if (!isPromptedRef.current) {
      // First idle event - show prompt
      console.log('[IdleTimer] Showing prompt dialog');
      isPromptedRef.current = true;
      setIsPrompted(true);
      onPromptRef.current();

      // Pause the idle timer so user activity doesn't reset it while prompt is shown
      if (pauseRef.current) {
        pauseRef.current();
      }

      // Set timeout for auto logout if no action taken
      promptTimeoutRef.current = setTimeout(() => {
        console.log('[IdleTimer] Auto logout triggered after prompt timeout');
        isPromptedRef.current = false;
        setIsPrompted(false);
        onIdleRef.current();
      }, promptTime);
    } else {
      // Already prompted, now logout
      console.log('[IdleTimer] Logging out (already prompted)');
      isPromptedRef.current = false;
      setIsPrompted(false);
      if (promptTimeoutRef.current) {
        clearTimeout(promptTimeoutRef.current);
        promptTimeoutRef.current = null;
      }
      onIdleRef.current();
    }
  }, [promptTime]);

  const handleOnActive = useCallback(() => {
    // User became active, but if prompt is already shown, don't dismiss it
    // The prompt should only be dismissed by user clicking Continue/Logout or by timeout
    // Only reset if prompt is NOT shown (normal idle timer reset)
    if (!isPromptedRef.current) {
      // Normal case: user is active and no prompt is shown, timer will reset automatically
      // No action needed here
    }
    // If prompt IS shown, we ignore the active event - user must click a button
  }, []);

  const { reset, pause, resume, getRemainingTime, getLastActiveTime } = useIdleTimer({
    timeout: idleTime,
    onIdle: handleOnIdle,
    onActive: handleOnActive,
    debounce: 250,
    crossTab: false, // Don't sync across tabs
    disabled: !enabled,
  });

  // Store pause/reset/resume in refs so they can be used in callbacks
  useEffect(() => {
    pauseRef.current = pause;
    resumeRef.current = resume;
    resetRef.current = reset;
  }, [pause, resume, reset]);

  // Debug: Log timer status
  useEffect(() => {
    if (enabled) {
      console.log('[IdleTimer] Timer enabled:', {
        idleTime,
        idleTimeSeconds: idleTime / 1000,
        promptTime,
        promptTimeSeconds: promptTime / 1000,
        remainingTime: getRemainingTime(),
        lastActiveTime: getLastActiveTime(),
      });
    }
  }, [enabled, idleTime, promptTime, getRemainingTime, getLastActiveTime]);

  // Reset timer when enabled state changes
  useEffect(() => {
    if (enabled) {
      console.log('[IdleTimer] Resetting timer (enabled)');
      reset();
    } else {
      console.log('[IdleTimer] Pausing timer (disabled)');
      pause();
      // Clear any pending prompts
      if (isPromptedRef.current) {
        isPromptedRef.current = false;
        setIsPrompted(false);
        if (promptTimeoutRef.current) {
          clearTimeout(promptTimeoutRef.current);
          promptTimeoutRef.current = null;
        }
      }
    }
  }, [enabled, reset, pause]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (promptTimeoutRef.current) {
        clearTimeout(promptTimeoutRef.current);
      }
    };
  }, []);

  const continueSession = useCallback(() => {
    isPromptedRef.current = false;
    setIsPrompted(false);
    if (promptTimeoutRef.current) {
      clearTimeout(promptTimeoutRef.current);
      promptTimeoutRef.current = null;
    }
    // Resume the timer and reset it
    if (resumeRef.current) {
      resumeRef.current();
    }
    if (resetRef.current) {
      resetRef.current(); // Reset the idle timer
    }
  }, []);

  const logout = useCallback(() => {
    isPromptedRef.current = false;
    setIsPrompted(false);
    if (promptTimeoutRef.current) {
      clearTimeout(promptTimeoutRef.current);
      promptTimeoutRef.current = null;
    }
    onIdleRef.current();
  }, []);

  return {
    isPrompted,
    continueSession,
    logout,
    reset,
    pause,
    resume,
  };
}
